import React from 'react';
import { useTranslation } from 'react-i18next';
import { Clock } from 'lucide-react';
import { Card, CardContent } from '../../../../../components/ui/Card';
import { WpsDeadlineView } from '../../../../../models/payroll';
import { LawReference } from '../../../ComplianceCenter/components/LawReference';

const zoneColors: Record<string, string> = {
  Green: 'bg-green-100 text-green-800 border-green-200',
  Amber: 'bg-amber-100 text-amber-800 border-amber-200',
  Red: 'bg-red-100 text-red-800 border-red-200',
  Overdue: 'bg-red-200 text-red-900 border-red-300',
};

/** Neutral card with a coloured zone badge instead of a full-width banner, so it does not drown out the alerts above. */
export const KsaDeadlineCard: React.FC<{ isKsa: boolean; deadline: WpsDeadlineView }> = ({ isKsa, deadline }) => {
  const { t } = useTranslation();
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-muted-foreground" />
            <div>
              <div className="flex items-center gap-2">
                <p className="font-semibold text-foreground">
                  {isKsa
                    ? (deadline.isRunPayrollMode
                      ? t('payroll.complianceCenter.wps.ksaRunPayrollTitle', 'Run Payroll: Mudad Clock Pending')
                      : t('payroll.complianceCenter.wps.ksaDeadlineStatus', 'Mudad Deadline Status'))
                    : t('payroll.wps.deadlineStatus', 'WPS Deadline Status')}
                </p>
                <LawReference
                  citations={[
                    {
                          code: 'HRSD March 2025 announcement / KSA Labor Law Art. 90',
                          description: t('payroll.complianceCenter.lawRef.mudadDeadline', 'KSA employers have 30 days from the wage due date to upload wages to Mudad. Missed deadlines downgrade Nitaqat band, block Iqama renewals, and record a non-compliance violation.'),
                        },
                  ]}
                />
              </div>
              <p className="text-sm text-muted-foreground">
                {deadline.isSubmitted
                  ? (isKsa ? t('payroll.complianceCenter.wps.ksaUploaded', 'Uploaded to Mudad') : t('payroll.wps.submitted', 'Submitted'))
                  : `${deadline.daysElapsed} ${t('payroll.wps.daysElapsed', 'days elapsed')} · ${deadline.daysRemaining} ${t('payroll.wps.daysRemaining', 'days remaining')}`}
              </p>
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${zoneColors[deadline.zone] ?? zoneColors.Green}`}>
            {deadline.isSubmitted
              ? (isKsa ? t('payroll.complianceCenter.wps.ksaUploadedBadge', 'Uploaded') : t('payroll.wps.submittedBadge', 'Submitted'))
              : deadline.zone}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};
